import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, AppDispatch } from "store";
import { AccountAdminState } from "./accountAdminTypes";
import {
  getRestAccountNotifications,
  getRestAccounts,
} from "@api/accountAdminStore";
import { AccountNotifications, AccountResponse } from "lib/ShiOneClient";
import GlobalConstants from "@constants";

const initialState: AccountAdminState = {
  accountsFetchStatus: GlobalConstants.fetchStatus.idle,
  accounts: [],
  accountNotificationsFetchStatus: GlobalConstants.fetchStatus.idle,
  accountNotifications: {} as AccountNotifications,
  createUserStatus: GlobalConstants.fetchStatus.idle,
  updateUserStatus: GlobalConstants.fetchStatus.idle,
};

const accountAdminSlice = createSlice({
  name: "accountAdmin",
  initialState,
  reducers: {
    fetchingAccounts(state) {
      state.accountsFetchStatus = GlobalConstants.fetchStatus.loading;
    },
    fetchedAccounts(state, action: PayloadAction<AccountResponse>) {
      state.accounts = action.payload.accounts ?? [];
      state.accountsFetchStatus = GlobalConstants.fetchStatus.complete;
    },
    fetchAccountsError(state) {
      state.accountsFetchStatus = GlobalConstants.fetchStatus.error;
    },
    fetchingAccountNotifications(state) {
      state.accountNotificationsFetchStatus =
        GlobalConstants.fetchStatus.loading;
    },
    fetchedAccountNotifications(
      state,
      action: PayloadAction<AccountNotifications>,
    ) {
      state.accountNotifications = action.payload;
      state.accountNotificationsFetchStatus =
        GlobalConstants.fetchStatus.complete;
    },
    fetchAccountNotificationsError(state) {
      state.accountNotificationsFetchStatus = GlobalConstants.fetchStatus.error;
    },
    createUserStatusReset(state) {
      state.createUserStatus = initialState.createUserStatus;
    },
    resetUpdateUser(state) {
      state.updateUserStatus = GlobalConstants.fetchStatus.idle;
    },
  },
});

export const getAccounts = (): AppThunk => async (dispatch: AppDispatch) => {
  dispatch(accountAdminSlice.actions.fetchingAccounts());
  try {
    const accountResponse = await getRestAccounts();
    dispatch(accountAdminSlice.actions.fetchedAccounts(accountResponse));
  } catch {
    dispatch(accountAdminSlice.actions.fetchAccountsError());
  }
};

export const getAccountNotifications =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(accountAdminSlice.actions.fetchingAccountNotifications());
    try {
      const accountNotifications = await getRestAccountNotifications();
      dispatch(
        accountAdminSlice.actions.fetchedAccountNotifications(
          accountNotifications,
        ),
      );
    } catch {
      dispatch(accountAdminSlice.actions.fetchAccountNotificationsError());
    }
  };

export const resetCurrentCreateUser =
  (): AppThunk => (dispatch: AppDispatch) => {
    dispatch(accountAdminSlice.actions.createUserStatusReset());
  };

export const resetUpdateUser = (): AppThunk => (dispatch: AppDispatch) => {
  dispatch(accountAdminSlice.actions.resetUpdateUser());
};

export const selectAccounts = (state) => state.accountAdminRtk.accounts;
export const selectAccountNotifications = (state) =>
  state.accountAdminRtk.accountNotifications;

export default accountAdminSlice.reducer;
